import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Category, CartItem } from '../types';
import { productsAPI, ordersAPI, settingsAPI, giftCodesAPI, loyaltyCodesAPI, realtimeCartAPI, payconiqAPI } from 'services/api';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button, Modal } from './ui';
import { printStandardReceipt } from '../utils/printReceipt';
import { secureGetItem } from '../utils/secureStorage';
import { getCurrentTimeBelgiumString } from '../utils/dateUtils';
// Lazy loading imports removed for now

// Product tipini burada tanımlayalım
interface Product {
  id: number;
  name: string;
  price: number;
  tax_rate: number;
  category_id: number;
  image_url?: string | null;
  stock_quantity?: number | null;
  is_active: boolean;
  created_at: string;
  category?: Category;
}

const QuickOrder: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageTitle] = useState('🎉 Gold Cafe T');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState<'nakit' | 'kart' | 'qr' | 'sumup'>('nakit');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isWaitingForPosResponse, setIsWaitingForPosResponse] = useState(false);

  // SumUp kart ödeme state'leri
  const [isWaitingForCardPayment, setIsWaitingForCardPayment] = useState(false);
  const [sumupCheckoutId, setSumupCheckoutId] = useState<string | null>(null);

  // Para üstü hesaplama
  const [customerPaid, setCustomerPaid] = useState<string>('');

  // QR Payment states
  const [qrPaymentData, setQrPaymentData] = useState<any>(null);
  const [qrPaymentStatus, setQrPaymentStatus] = useState<string>('');
  const [isQrPaymentActive, setIsQrPaymentActive] = useState(false);
  const [qrPaymentInterval, setQrPaymentInterval] = useState<NodeJS.Timeout | null>(null);
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);
  const [qrPaymentCancelledByUser, setQrPaymentCancelledByUser] = useState(false); // Kullanıcı tarafından iptal edildi mi?
  const [completedOrder, setCompletedOrder] = useState<any>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productNote, setProductNote] = useState('');
  const [wantsReceipt, setWantsReceipt] = useState(false);

  // Müşteri ekranı artık hep açık - state gerekmez

  // Hediye kodu state'leri
  const [giftCode, setGiftCode] = useState('');
  const [appliedGiftCode, setAppliedGiftCode] = useState<any>(null);
  const [giftCodeDiscount, setGiftCodeDiscount] = useState(0);
  const [isValidatingCode, setIsValidatingCode] = useState(false);

  // Stok bildirimi state'leri
  const [lowStockNotification, setLowStockNotification] = useState<{
    products: { id: number; name: string; stock_quantity: number; category_name: string }[];
    show: boolean;
  } | null>(null);
  const lowStockTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ref'ler - setState asenkron olduğu için değerleri ref'te de tutuyoruz
  const appliedGiftCodeRef = useRef<any>(null);
  const giftCodeDiscountRef = useRef<number>(0);

  // Sadakat kodu state'leri
  const [loyaltyCode, setLoyaltyCode] = useState('');
  const [appliedLoyaltyCode, setAppliedLoyaltyCode] = useState<any>(null);
  const [tempLoyaltyCount, setTempLoyaltyCount] = useState(0);
  const [isValidatingLoyaltyCode, setIsValidatingLoyaltyCode] = useState(false);
  const [pendingLoyaltyReward, setPendingLoyaltyReward] = useState<any>(null); // Bekleyen ödül kullanımı

  const [receiptSettings, setReceiptSettings] = useState({
    receiptTitle: '',
    receiptSubtitle: '',
    receiptPhone: '',
    receiptTaxNumber: '',
    receiptFooter: '',
    receiptDateLabel: '',
    receiptTimeLabel: '',
    receiptOrderNumberLabel: '',
    receiptOrderDetailsLabel: '',
    receiptPaymentLabel: '',
    receiptPaymentCash: '',
    receiptPaymentCard: '',
    receiptSubtotalLabel: '',
    receiptTaxLabel: '',
    receiptTotalLabel: '',
    receiptQuantityLabel: '',
    receiptShowTaxDetails: false,
    receiptShowPaymentType: false,
    receiptShowOrderNumber: false,
    receiptShowDateTime: false
  });

  const { user, logout: originalLogout } = useAuth();

  // Logout fonksiyonunu override et - sadece local state'i temizle
  const logout = async () => {
    // Sadece local state'i temizle (backend endpoint'ini çağırma)
    setCart([]);
    setAppliedGiftCode(null);
    setGiftCode('');
    appliedGiftCodeRef.current = null;
    giftCodeDiscountRef.current = 0;
    setAppliedLoyaltyCode(null);
    setLoyaltyCode('');
    setTempLoyaltyCount(0);
    setPendingLoyaltyReward(null);

    // Orijinal logout fonksiyonunu çağır
    originalLogout();
  };
  const { formatPrice } = useCurrency();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();

  // Component unmount olduğunda sepet state'ini temizle
  useEffect(() => {
    return () => {
      // Component unmount olduğunda backend'deki sepet state'ini temizle
      const clearCartOnUnmount = async () => {
        try {
          const token = secureGetItem('token');
          if (token) {
            // CSRF token'ı al
            const csrfResponse = await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/csrf-token`, {
              credentials: 'include'
            });
            const csrfData = await csrfResponse.json();

            await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/realtime-cart/clear`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-CSRF-Token': csrfData.csrfToken
              },
              credentials: 'include'
            });
          }
        } catch (error) {
          // Component unmount sepet temizleme hatası
        }
      };

      clearCartOnUnmount();

      // QR ödeme interval'ını temizle
      if (qrPaymentInterval) {
        clearInterval(qrPaymentInterval);
      }
    };
  }, [qrPaymentInterval]);

  // Sayfa değişikliği dinleme
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Sayfa kapatılırken sepet state'ini temizle
      const clearCartOnPageLeave = async () => {
        try {
          const token = secureGetItem('token');
          if (token) {
            await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/realtime-cart/clear`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            });
          }
        } catch (error) {
          // Sayfa kapatma sepet temizleme hatası
        }
      };

      clearCartOnPageLeave();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const calculateTotal = useCallback(() => {
    return cart.reduce((total, item) => {
      // Ödül ürünleri ücretsiz
      if (item.notes === '🎁 ÖDÜL') {
        return total;
      }
      return total + (item.product.price * item.quantity);
    }, 0);
  }, [cart]);

  // Vergi hesaplama (Fiyata Dahil Mod - Belçika)
  // Fiyatlar zaten vergili, vergi tutarını hesapla: fiyat - (fiyat / (1 + vergi_orani/100))
  const calculateTax = () => {
    return cart.reduce((total, item) => {
      // Ödül ürünleri ücretsiz, vergi de yok
      if (item.notes === '🎁 ÖDÜL') {
        return total;
      }
      const itemTotal = item.product.price * item.quantity;
      const taxRate = item.product.tax_rate || 0;
      const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
      return total + taxAmount;
    }, 0);
  };

  const calculateGrandTotal = () => {
    return calculateTotal(); // Fiyatlar zaten vergili
  };

  // Hediye kodu fonksiyonları
  const validateGiftCode = async () => {
    console.log('🎁 validateGiftCode çağrıldı, giftCode:', giftCode);

    if (!giftCode.trim()) {
      toast.error('Lütfen bir hediye kodu girin');
      return;
    }

    setIsValidatingCode(true);
    try {
      const cartTotal = calculateGrandTotal();
      const cartItems = cart.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
        unit_price: item.product.price
      }));

      console.log('🔍 Validasyon isteği gönderiliyor:', { giftCode, cartTotal, cartItems });

      const validation = await giftCodesAPI.validate(giftCode.trim(), cartTotal, cartItems);

      console.log('✅ Validasyon sonucu:', validation);

      if (validation.valid && validation.giftCode) {
        // İndirim miktarını hesapla
        let discountAmount = 0;
        if (validation.giftCode.discount_type === 'percentage') {
          discountAmount = (cartTotal * validation.giftCode.discount_value) / 100;
        } else {
          discountAmount = validation.giftCode.discount_value;
        }

        console.log('✅ Hediye kodu geçerli, state ve ref set ediliyor:', {
          giftCode: validation.giftCode,
          discount_amount: discountAmount
        });

        // Hem state hem de ref'i set et
        setAppliedGiftCode(validation.giftCode);
        setGiftCodeDiscount(discountAmount);
        appliedGiftCodeRef.current = validation.giftCode;
        giftCodeDiscountRef.current = discountAmount;

        console.log('✅ State ve ref set edildi');

        toast.success(`✅ ${validation.message}`);
        // useEffect otomatik olarak müşteri ekranını güncelleyecek
      }
    } catch (error: any) {
      console.error('❌ Hediye kodu doğrulama hatası:', error);
      toast.error(error.response?.data?.error || 'Kod doğrulanamadı');
      setAppliedGiftCode(null);
      setGiftCodeDiscount(0);
      appliedGiftCodeRef.current = null;
      giftCodeDiscountRef.current = 0;
    } finally {
      setIsValidatingCode(false);
    }
  };

  // Sadakat kodu fonksiyonları
  const validateLoyaltyCode = async () => {
    if (!loyaltyCode.trim()) {
      toast.error('Lütfen bir sadakat kodu girin');
      return;
    }

    // Sepet boşken de sadakat kodu sorgulanabilir

    setIsValidatingLoyaltyCode(true);
    try {
      // Sadakat kodu doğrulama - fresh data ile
      const foundCode = await loyaltyCodesAPI.validate(loyaltyCode.trim());

      // Kod durumunu kontrol et
      if (foundCode.reward_used_at) {
        toast.error('❌ Bu ödül zaten kullanılmış');
        setAppliedLoyaltyCode(null);
        return;
      }

      // console.log('🔍 Bulunan sadakat kodu:', {
      //   code: foundCode.code,
      //   current_count: foundCode.current_count,
      //   required_quantity: foundCode.required_quantity,
      //   is_completed: foundCode.is_completed,
      //   rule_name: foundCode.rule_name,
      //   reward_product_name: foundCode.reward_product_name
      // });
      // console.log('🔍 Tüm foundCode objesi:', foundCode);

      if (foundCode.is_completed && !foundCode.reward_used_at) {
        // Ödül kazanılmış ama kullanılmamış

        setAppliedLoyaltyCode(foundCode);
        toast.success(`🎉 Sadakat ödülü kullanılabilir! ${foundCode.reward_product_name}`);
        // useEffect otomatik olarak müşteri ekranını güncelleyecek
      } else if (foundCode.is_completed && foundCode.reward_used_at) {
        toast.error('❌ Bu ödül zaten kullanılmış');
        setAppliedLoyaltyCode(null);
      } else {
        // Henüz tamamlanmamış - geçici sayım ile göster
        const targetProductQuantity = cart.reduce((total, item) => {
          return item.product.id === foundCode.target_product_id
            ? total + item.quantity
            : total;
        }, 0);

        const tempCount = targetProductQuantity > 0 ? targetProductQuantity : 1; // En az 1 sayım göster
        const newTotalCount = foundCode.current_count + tempCount;

        setAppliedLoyaltyCode(foundCode);
        setTempLoyaltyCount(tempCount);

        console.log('🔍 Sadakat kodu geçici sayım:', {
          currentCount: foundCode.current_count,
          tempCount,
          newTotalCount,
          targetProductId: foundCode.target_product_id,
          cartTargetQuantity: targetProductQuantity
        });

        if (newTotalCount >= foundCode.required_quantity) {
          toast.success(`🎉 Sadakat kodu tamamlanacak: ${newTotalCount}/${foundCode.required_quantity} - Sipariş tamamlandığında ödül kazanılacak!`);
        } else {
          toast.success(`📊 Sadakat kodu eklendi: ${newTotalCount}/${foundCode.required_quantity} - Sipariş tamamlandığında sayım artacak`);
        }
      }
    } catch (error: any) {
      toast.error(`Kod doğrulanamadı: ${error.message || 'Bilinmeyen hata'}`);
      setAppliedLoyaltyCode(null);
    } finally {
      setIsValidatingLoyaltyCode(false);
    }
  };

  // Sadakat kodunu kaldır (hem UI'dan gizle hem müşteri ekranından kaldır)
  const removeLoyaltyCode = async () => {
    console.log('🗑️ removeLoyaltyCode çağrıldı');

    // Önce ödül ürünlerini sepetten kaldır
    setCart(prevCart => {
      const filteredCart = prevCart.filter(item => item.notes !== '🎁 ÖDÜL');
      console.log('🗑️ Ödül ürünleri sepetten kaldırıldı:', {
        önceki: prevCart.length,
        sonraki: filteredCart.length
      });
      return filteredCart;
    });

    // State'leri temizle
    setAppliedLoyaltyCode(null);
    setTempLoyaltyCount(0);
    setLoyaltyCode('');
    setPendingLoyaltyReward(null);

    // Hemen gerçek zamanlı sepeti güncelle (sadakat kodu olmadan)
    // appliedLoyaltyCode = null olarak gönder
    try {
      const payload = {
        items: cart.map(item => ({
          id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          unit_price: item.product.price,
          notes: item.notes,
          is_reward: item.notes === '🎁 ÖDÜL'
        })),
        totals: {
          subtotal: cart.reduce((total, item) => {
            if (item.notes === '🎁 ÖDÜL') return total;
            return total + (item.product.price * item.quantity);
          }, 0),
          tax: cart.reduce((total, item) => {
            if (item.notes === '🎁 ÖDÜL') return total;
            const itemSubtotal = item.product.price * item.quantity;
            const taxRate = item.product.tax_rate || 0;
            return total + (itemSubtotal * (taxRate / 100));
          }, 0),
          total: cart.reduce((total, item) => {
            if (item.notes === '🎁 ÖDÜL') return total;
            const itemSubtotal = item.product.price * item.quantity;
            const taxRate = item.product.tax_rate || 0;
            return total + itemSubtotal + (itemSubtotal * (taxRate / 100));
          }, 0) - giftCodeDiscount,
          discount: giftCodeDiscount
        },
        enabled: true,
        coupons: {
          giftCode: appliedGiftCode ? {
            code: appliedGiftCode.code,
            discount: appliedGiftCode.discount || 0,
            type: appliedGiftCode.type
          } : null,
          loyaltyCode: null // Sadakat kodu kaldırıldı
        },
        preserveQrPayment: true
      };

      await realtimeCartAPI.update(payload);
      console.log('✅ Sadakat kodu müşteri ekranından kaldırıldı');
    } catch (error) {
      console.error('❌ Sadakat kodu kaldırma güncelleme hatası:', error);
    }

    toast.success('✅ Sadakat kodu kaldırıldı');
  };

  const useLoyaltyReward = async () => {
    // Kod zaten tamamlanmışsa veya geçici sayım ile tamamlanmışsa
    const isCompleted = appliedLoyaltyCode.is_completed ||
      (tempLoyaltyCount > 0 && (appliedLoyaltyCode.current_count + tempLoyaltyCount) >= appliedLoyaltyCode.required_quantity);

    console.log('🔍 useLoyaltyReward kontrolü:', {
      isCompleted: appliedLoyaltyCode.is_completed,
      tempLoyaltyCount,
      currentCount: appliedLoyaltyCode.current_count,
      requiredQuantity: appliedLoyaltyCode.required_quantity,
      calculatedIsCompleted: isCompleted,
      rewardUsedAt: appliedLoyaltyCode.reward_used_at
    });

    if (!appliedLoyaltyCode || !isCompleted || appliedLoyaltyCode.reward_used_at || pendingLoyaltyReward) {
      console.log('❌ Ödül kullanılamıyor:', {
        hasCode: !!appliedLoyaltyCode,
        isCompleted,
        rewardUsedAt: appliedLoyaltyCode?.reward_used_at,
        pendingLoyaltyReward: !!pendingLoyaltyReward
      });
      toast.error('Kullanılabilir ödül bulunamadı veya ödül zaten kullanılmış/beklemede');
      return;
    }

    try {
      // Ödülü sepete ekle (ücretsiz) - SADECE sepete ekle, backend'e kaydetme
      const rewardProduct = products.find(p => p.id === appliedLoyaltyCode.reward_product_id);

      // Eğer bulunamazsa, ürün adı ile dene
      if (!rewardProduct) {
        const rewardProductByName = products.find(p => p.name === appliedLoyaltyCode.reward_product_name);
        if (rewardProductByName) {
          const updatedRewardProduct = rewardProductByName;

          // Ödülü sepete ekle (ücretsiz)
          setCart(prevCart => {
            const existingItem = prevCart.find(item =>
              item.product.id === updatedRewardProduct.id &&
              item.notes === '🎁 ÖDÜL'
            );

            if (existingItem) {
              return prevCart.map(item =>
                item.product.id === updatedRewardProduct.id && item.notes === 'ÜCRETSİZ'
                  ? { ...item, quantity: item.quantity + 1 }
                  : item
              );
            } else {
              return [...prevCart, {
                product: updatedRewardProduct,
                quantity: 1,
                notes: '🎁 ÖDÜL'
              }];
            }
          });

          toast.success(`🎉 ${updatedRewardProduct.name} ücretsiz eklendi! Sepeti tamamladığınızda ödül kullanılacak.`);

          // Bekleyen ödül kullanımını kaydet
          setPendingLoyaltyReward({
            loyaltyCodeId: appliedLoyaltyCode.id,
            productId: updatedRewardProduct.id,
            tempLoyaltyCount: tempLoyaltyCount
          });

          // NOT: reward_used_at backend'e kaydetme işlemi ödeme tamamlandığında yapılacak
          // Frontend'de sadece pendingLoyaltyReward ile takip ediyoruz

          return;
        }
      }

      if (rewardProduct) {
        // Ödülü sepete ekle (ücretsiz)
        setCart(prevCart => {
          const existingItem = prevCart.find(item =>
            item.product.id === rewardProduct.id &&
            item.notes === '🎁 ÖDÜL'
          );

          if (existingItem) {
            return prevCart.map(item =>
              item.product.id === rewardProduct.id && item.notes === '🎁 ÖDÜL'
                ? { ...item, quantity: item.quantity + 1 }
                : item
            );
          } else {
            return [...prevCart, {
              product: rewardProduct,
              quantity: 1,
              notes: '🎁 ÖDÜL'
            }];
          }
        });

        toast.success(`🎉 ${rewardProduct.name} ücretsiz eklendi! Sepeti tamamladığınızda ödül kullanılacak.`);

        // Bekleyen ödül kullanımını kaydet
        setPendingLoyaltyReward({
          loyaltyCodeId: appliedLoyaltyCode.id,
          productId: rewardProduct.id,
          tempLoyaltyCount: tempLoyaltyCount
        });

        // NOT: reward_used_at backend'e kaydetme işlemi ödeme tamamlandığında yapılacak
        // Frontend'de sadece pendingLoyaltyReward ile takip ediyoruz
      } else {
        toast.error('❌ Ödül ürünü bulunamadı!');
        return;
      }
    } catch (error: any) {
      console.error('❌ Ödül ekleme hatası:', error);
      toast.error('❌ Ödül sepete eklenemedi');
    }
  };

  const removeGiftCode = () => {
    setGiftCode('');
    setAppliedGiftCode(null);
    setGiftCodeDiscount(0);
    appliedGiftCodeRef.current = null;
    giftCodeDiscountRef.current = 0;
    toast.success('Hediye kodu kaldırıldı');
    // useEffect otomatik olarak müşteri ekranını güncelleyecek
  };

  const calculateFinalTotal = () => {
    const subtotal = calculateGrandTotal();
    return Math.max(0, subtotal - giftCodeDiscount);
  };

  const calculateChange = () => {
    const finalTotal = calculateFinalTotal();
    const paid = parseFloat(customerPaid) || 0;
    return Math.max(0, paid - finalTotal);
  };

  const openNoteModal = (product: Product) => {
    setSelectedProduct(product);
    setProductNote('');
    setShowNoteModal(true);
  };

  const addToCartWithNote = () => {
    if (selectedProduct) {
      addToCart(selectedProduct, productNote);
      setShowNoteModal(false);
      setSelectedProduct(null);
      setProductNote('');
    }
  };

  const addToCart = (product: Product, notes?: string) => {
    // Stok kontrolü
    if (product.stock_quantity !== null && product.stock_quantity !== undefined) {
      if (product.stock_quantity <= 0) {
        toast.error(`${product.name} stokta bulunmuyor!`);
        return;
      }

      // Sepetteki mevcut miktarı kontrol et
      const existingItem = cart.find(item =>
        item.product.id === product.id && item.notes === (notes || '')
      );

      const currentQuantity = existingItem ? existingItem.quantity : 0;
      if (currentQuantity >= product.stock_quantity) {
        toast.error(`${product.name} için yeterli stok yok! (Mevcut: ${product.stock_quantity} adet)`);
        return;
      }
    }

    setCart(prevCart => {
      const existingItem = prevCart.find(item =>
        item.product.id === product.id &&
        item.notes === (notes || '')
      );

      let newCart: typeof prevCart;
      if (existingItem) {
        newCart = prevCart.map(item =>
          item.product.id === product.id && item.notes === (notes || '')
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        newCart = [...prevCart, {
          product,
          quantity: 1,
          notes: notes || ''
        }];
      }

      // useEffect otomatik olarak müşteri ekranını güncelleyecek

      return newCart;
    });

    // Sadakat kodu sayımını güncelle (eğer aktif sadakat kodu varsa)
    if (appliedLoyaltyCode && !appliedLoyaltyCode.is_completed) {
      // Eğer eklenen ürün sadakat kuralının hedef ürünüyse sayımı artır
      if (appliedLoyaltyCode.target_product_id === product.id) {
        const newCount = (appliedLoyaltyCode.current_count || 0) + 1;
        const isCompleted = newCount >= appliedLoyaltyCode.required_quantity;

        setAppliedLoyaltyCode({
          ...appliedLoyaltyCode,
          current_count: newCount,
          is_completed: isCompleted,
          reward_earned_at: isCompleted ? new Date().toISOString() : appliedLoyaltyCode.reward_earned_at
        });

        if (isCompleted) {
          toast.success(`🎉 Sadakat kodu tamamlandı! Ödül kazanıldı: ${appliedLoyaltyCode.reward_product_name}`);
        } else {
          toast.success(`📊 Sadakat kodu güncellendi: ${newCount}/${appliedLoyaltyCode.required_quantity}`);
        }
      }
    }

    toast.success(`${product.name} sepete eklendi`);
  };

  const updateCartItemQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }

    setCart(prevCart => {
      const newCart = prevCart.map((item, i) =>
        i === index
          ? { ...item, quantity }
          : item
      );

      // State güncellendikten hemen sonra gerçek zamanlı güncellemeyi gönder
      setTimeout(() => {
        updateRealtimeCartWithData(newCart);
      }, 0);

      return newCart;
    });
  };

  const removeFromCart = (index: number) => {
    setCart(prevCart => {
      const newCart = prevCart.filter((_, i) => i !== index);

      // State güncellendikten hemen sonra gerçek zamanlı güncellemeyi gönder
      setTimeout(() => {
        updateRealtimeCartWithData(newCart);
      }, 0);

      return newCart;
    });
  };

  const clearCart = async (preserveQrPayment = true) => {
    setCart([]);
    // Sepet temizlendiğinde sadakat kodu durumunu da temizle
    setAppliedLoyaltyCode(null);
    setLoyaltyCode('');
    setTempLoyaltyCount(0); // Geçici sayımı sıfırla
    setPendingLoyaltyReward(null); // Bekleyen ödül kullanımını temizle

    // Müşteri ekranına sepet güncellemesini gönder
    try {
      await updateRealtimeCartWithData([], true, preserveQrPayment); // Müşteri ekranı hep açık
    } catch (error) {
      console.error('❌ Sepet temizleme sırasında müşteri ekranı güncelleme hatası:', error);
    }
  };

  const startPaymentProcess = () => {
    if (cart.length === 0) {
      toast.error('Sepet boş!');
      return;
    }
    // İşlem state'ini sıfırla (önceki işlemden kalabilir)
    setIsProcessingPayment(false);
    setShowPaymentModal(true);
  };

  // SumUp ödeme durumu kontrol et (sadece DB'den - webhook tabanlı, polling yok)
  const checkSumUpPaymentStatus = async (isManual = false) => {
    if (!currentOrderId || !sumupCheckoutId) return;

    try {
      if (isManual) {
        toast.loading('Durum kontrol ediliyor...', { id: 'check-status' });
      }

      const status = await ordersAPI.getSumUpStatus(
        currentOrderId,
        appliedGiftCodeRef.current?.id,
        giftCodeDiscountRef.current
      );

      if (isManual) {
        toast.dismiss('check-status');
      }

      console.log('🔍 [QuickOrder] SumUp Status Response (DB):', {
        orderId: currentOrderId,
        status: status,
        completed: status.completed,
        statusValue: status.status
      });

      if (status.completed) {
        // Ödeme tamamlandı
        setIsWaitingForCardPayment(false);
        setSumupCheckoutId(null);

        // Toast loading'i kapat
        toast.dismiss('card-payment-waiting');

        // 🔊 Başarı sesi çal
        try {
          const successSound = new Audio('/sounds/success.mp3');
          successSound.volume = 0.7; // %70 ses seviyesi
          successSound.play().catch(err => console.log('Ses çalınamadı:', err));
        } catch (soundError) {
          console.log('Ses dosyası yüklenemedi:', soundError);
        }

        if (appliedGiftCodeRef.current && giftCodeDiscountRef.current > 0) {
          toast.success(`🎁 Hediye kodu kullanıldı: -${formatPrice(giftCodeDiscountRef.current)}`);
        }

        toast.success('💳 Kart ödemesi başarıyla tamamlandı!');

        // Sipariş bilgilerini al
        const completedOrderData = await ordersAPI.getById(currentOrderId);

        setCompletedOrder({
          ...completedOrderData,
          payment_type: 'kart',
          completed_at: new Date().toISOString()
        });

        // Modal'ı kapat, receipt modal'ı aç
        setShowPaymentModal(false);
        setShowReceiptModal(true);

        // Sepeti temizle
        await clearCart();

        // Hediye kodu state'lerini ve ref'lerini temizle
        setGiftCode('');
        setAppliedGiftCode(null);
        setGiftCodeDiscount(0);
        appliedGiftCodeRef.current = null;
        giftCodeDiscountRef.current = 0;

        // Sadakat kodu state'lerini temizle
        setLoyaltyCode('');
        setAppliedLoyaltyCode(null);
        setTempLoyaltyCount(0);
        setPendingLoyaltyReward(null);

        // Ürünleri yeniden yükle (stok güncellemesi için)
        try {
          const allProductsData = await productsAPI.getAll();
          setAllProducts(allProductsData);

          if (selectedCategory !== null) {
            const filteredProducts = allProductsData.filter(product =>
              product.category_id === selectedCategory && product.is_active
            );
            setProducts(filteredProducts);
          } else {
            const activeProducts = allProductsData.filter(product => product.is_active);
            setProducts(activeProducts);
          }
        } catch (error) {
          // console.error('Ürünler yeniden yüklenirken hata:', error);
        }

      } else if (status.status === 'FAILED' || status.status === 'CANCELLED' || status.status === 'EXPIRED') {
        // Ödeme başarısız
        setIsWaitingForCardPayment(false);
        setSumupCheckoutId(null);

        // Toast loading'i kapat
        toast.dismiss('card-payment-waiting');

        toast.error('Kart ödemesi başarısız oldu. Lütfen tekrar deneyin.');
        setIsProcessingPayment(false);
      } else {
        // PENDING/PROCESSING
        if (isManual) {
          toast('Ödeme henüz tamamlanmadı, lütfen POS cihazını kontrol edin.', { icon: '⏳' });
        }
      }
    } catch (error: any) {
      if (isManual) toast.dismiss('check-status');
      console.error('SumUp ödeme durumu kontrol hatası:', error);
      if (isManual) toast.error('Durum kontrol edilemedi');
    }
  };

  // SumUp ödeme durumu kontrolü - webhook tabanlı + polling fallback
  // Polling tekrar eklendi - Backend'den DB durumu sorgulanır (SumUp API değil)
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isWaitingForCardPayment && sumupCheckoutId && currentOrderId) {
      // İlk kontrol hemen yap
      checkSumUpPaymentStatus(false);

      // Her 4 saniyede bir kontrol et
      interval = setInterval(() => {
        checkSumUpPaymentStatus(false);
      }, 4000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isWaitingForCardPayment, sumupCheckoutId, currentOrderId]);

  // Sayfa focus olduğunda kontrol et (kullanıcı etkileşimi)
  useEffect(() => {
    const handleFocus = () => {
      if (isWaitingForCardPayment && currentOrderId) {
        checkSumUpPaymentStatus(false);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isWaitingForCardPayment, currentOrderId]);

  const processQuickPayment = async () => {
    console.log('🔍 processQuickPayment çağrıldı, isProcessingPayment:', isProcessingPayment);

    // ÖNEMLİ: Ref'ten değerleri al (state yerine ref kullanıyoruz çünkü setState asenkron)
    const currentAppliedGiftCode = appliedGiftCodeRef.current;
    const currentGiftCodeDiscount = giftCodeDiscountRef.current;

    console.log('💾 Hediye kodu REF değerleri:', {
      currentAppliedGiftCode,
      currentGiftCodeDiscount
    });

    // Tüm QR ödeme state'lerini temizle (güvenlik için)
    console.log('🧹 QR ödeme state\'leri temizleniyor...');
    setIsQrPaymentActive(false);
    setQrPaymentData(null);
    setQrPaymentStatus('');
    setCurrentOrderId(null);
    setIsProcessingPayment(false);

    // QR ödeme interval'ını temizle
    if (qrPaymentInterval) {
      clearInterval(qrPaymentInterval);
      setQrPaymentInterval(null);
    }

    // Kısa bir bekleme
    await new Promise(resolve => setTimeout(resolve, 100));

    console.log('✅ State\'ler temizlendi, işlem başlatılıyor...');

    try {
      console.log('🔄 processQuickPayment başlatılıyor...');
      setIsProcessingPayment(true);

      // Token'ın tam yüklenmesini bekle (ilk sipariş için)
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!user?.id) {
        toast.error('Kullanıcı bilgisi bulunamadı');
        return;
      }

      // Sadakat kodu için hedef ürün miktarını hesapla
      const loyaltyQuantity = appliedLoyaltyCode ? cart.reduce((total, item) => {
        return item.product.id === appliedLoyaltyCode.target_product_id
          ? total + item.quantity
          : total;
      }, 0) : 0;

      console.log('🔍 QuickOrder sadakat kodu sayım hesaplaması:', {
        targetProductId: appliedLoyaltyCode?.target_product_id,
        loyaltyQuantity,
        cartItems: cart.map(item => ({
          productId: item.product.id,
          quantity: item.quantity,
          isTarget: item.product.id === appliedLoyaltyCode?.target_product_id
        }))
      });

      const orderData = {
        table_id: 0,
        user_id: user.id,
        receipt_requested: wantsReceipt,
        loyalty_code_id: appliedLoyaltyCode?.id || null,
        loyalty_quantity: loyaltyQuantity,
        gift_code_id: currentAppliedGiftCode?.id || null,
        gift_code_discount: currentGiftCodeDiscount || 0,
        items: cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          unit_price: item.product.price,
          notes: item.notes || ''
        }))
      };

      console.log('🔄 Sipariş oluşturuluyor, orderData:', orderData);

      let newOrder: any;
      try {
        console.log('🔄 ordersAPI.create çağrılıyor...');
        newOrder = await ordersAPI.create(orderData);
        console.log('✅ Sipariş oluşturuldu, newOrder:', newOrder);
      } catch (error) {
        console.error('❌ ordersAPI.create hatası:', error);
        throw error;
      }

      // Toplam tutarı hesapla (ödül ürünlerini hariç tut)
      const subtotal = cart.reduce((total, item) => {
        if (item.notes === '🎁 ÖDÜL') return total;
        return total + (item.product.price * item.quantity);
      }, 0);
      // Fiyata Dahil Mod (Belçika) - Vergi fiyatın içinden çıkarılır
      const taxTotal = cart.reduce((total, item) => {
        if (item.notes === '🎁 ÖDÜL') return total;
        const itemSubtotal = item.product.price * item.quantity;
        const taxRate = item.product.tax_rate || 0;
        const taxAmount = itemSubtotal - (itemSubtotal / (1 + taxRate / 100));
        return total + taxAmount;
      }, 0);
      const totalAmount = subtotal - giftCodeDiscount; // Fiyatlar zaten vergili

      // QR ödeme için özel işlem
      if (paymentType === 'qr') {
        try {
          console.log('🔄 QR ödeme başlatılıyor:', {
            orderId: newOrder.id,
            amount: totalAmount,
            description: `Hızlı Sipariş #${newOrder.id}`
          });

          toast.loading('QR ödeme hazırlanıyor...', { id: 'qr-loading' });

          // Önce Payconiq servisini test et
          try {
            const testResult = await payconiqAPI.testService();
            console.log('🔍 Payconiq servis testi:', testResult);
          } catch (testError) {
            console.error('❌ Payconiq servis testi başarısız:', testError);
            toast.dismiss('qr-loading');
            toast.error('Payconiq servisi çalışmıyor. Lütfen backend\'i kontrol edin.');
            setIsProcessingPayment(false);
            return;
          }

          // Payconiq QR ödeme başlat
          const qrPaymentData = await payconiqAPI.initiatePayment(
            newOrder.id,
            totalAmount,
            `Hızlı Sipariş #${newOrder.id}`
          );

          // QR ödeme state'lerini ayarla
          setQrPaymentData(qrPaymentData);
          setQrPaymentStatus('PENDING');
          setIsQrPaymentActive(true);
          setCurrentOrderId(newOrder.id);
          setQrPaymentCancelledByUser(false); // Yeni QR ödeme başlatıldı, flag'i sıfırla

          // Debug: QR URL'sini kontrol et
          console.log('🔍 Frontend QR URL Debug:', {
            qrCodeUrl: qrPaymentData.qrCodeUrl,
            paymentId: qrPaymentData.paymentId,
            deepLinkUrl: qrPaymentData.deepLinkUrl,
            expiresAt: qrPaymentData.expiresAt
          });

          // Müşteri ekranına QR kodu gönder
          try {
            console.log('🚀 Müşteri ekranına QR payment gönderiliyor:', {
              orderId: newOrder.id,
              qrCodeUrl: qrPaymentData.qrCodeUrl,
              amount: totalAmount,
              description: `Hızlı Sipariş #${newOrder.id}`,
              expiresAt: qrPaymentData.expiresAt,
              paymentId: qrPaymentData.paymentId,
              deepLinkUrl: qrPaymentData.deepLinkUrl
            });

            const sendResult = await realtimeCartAPI.sendQRPayment({
              orderId: newOrder.id,
              qrCodeUrl: qrPaymentData.qrCodeUrl,
              amount: totalAmount,
              description: `Hızlı Sipariş #${newOrder.id}`,
              expiresAt: qrPaymentData.expiresAt,
              paymentId: qrPaymentData.paymentId,
              deepLinkUrl: qrPaymentData.deepLinkUrl
            });

            console.log('✅ QR payment müşteri ekranına gönderildi:', sendResult);
          } catch (error) {
            console.error('❌ QR kodu müşteri ekranına gönderilemedi:', error);
            toast.error('QR kodu müşteri ekranına gönderilemedi. Lütfen tekrar deneyin.');
          }

          toast.dismiss('qr-loading');
          toast.success('📱 QR ödeme başlatıldı! Müşteri ekranında QR kodu görünüyor.');

          // QR ödeme başlatıldı, modal açık kalacak
          setIsProcessingPayment(false); // Processing state'ini sıfırla

          // QR ödeme durumunu kontrol etmeye başla
          startQrPaymentStatusCheck(newOrder.id);

          return; // Normal ödeme işlemini durdur
        } catch (error: any) {
          console.error('❌ QR ödeme başlatma hatası:', error);
          toast.dismiss('qr-loading');
          toast.error(`❌ QR ödeme başlatılamadı: ${error?.message || error?.response?.data?.error || 'Bilinmeyen hata'}`);
          setIsProcessingPayment(false); // Hata durumunda processing state'ini sıfırla
          return;
        }
      }


      console.log('🔄 Ödeme işlemi başlatılıyor, orderId:', newOrder.id, 'paymentType:', paymentType);

      // SUMUP POS ÖDEMESİ: Backend'e 'sumup' gönder, POS cihazına yönlendir
      if (paymentType === 'sumup') {
        try {
          setIsWaitingForCardPayment(true);

          // Backend'e 'sumup' olarak gönder - SumUp POS'a yönlendirecek
          const paymentResponse = await ordersAPI.pay(
            newOrder.id,
            'sumup', // SumUp POS ödemesi
            currentAppliedGiftCode?.id,
            currentGiftCodeDiscount
          );

          console.log('💳 SumUp ödeme başlatıldı:', paymentResponse);

          // Backend'den checkoutId aldıysak kaydet
          if (paymentResponse && paymentResponse.checkoutId) {
            setSumupCheckoutId(paymentResponse.checkoutId);
          }

          // currentOrderId'yi ayarla - polling için gerekli
          setCurrentOrderId(newOrder.id);

          toast.loading('💳 POS cihazında ödeme başlatıldı. Lütfen kartı okutun...', {
            duration: 30000,
            id: 'card-payment-waiting'
          });

          setIsProcessingPayment(false);
          // Polling useEffect'te başlayacak (isWaitingForCardPayment true olduğu için)
          return; // Normal akışa devam etme, webhook bekleniyor
        } catch (error: any) {
          console.error('❌ SumUp ödeme hatası:', error);
          setIsWaitingForCardPayment(false);
          setSumupCheckoutId(null);
          setCurrentOrderId(null);
          toast.dismiss('card-payment-waiting');

          // ÖNEMLİ: SumUp ödeme başarısız olduğunda siparişi iptal et ve sil!
          // Böylece mutfağa sipariş gitmez
          try {
            console.log('🗑️ SumUp hatası nedeniyle sipariş siliniyor:', newOrder.id);
            await ordersAPI.delete(newOrder.id);
            console.log('✅ Sipariş başarıyla silindi');
          } catch (deleteError) {
            console.error('❌ Sipariş silinirken hata:', deleteError);
            // Silme başarısız olursa iptal etmeyi dene
            try {
              await ordersAPI.cancel(newOrder.id);
              console.log('✅ Sipariş iptal edildi');
            } catch (cancelError) {
              console.error('❌ Sipariş iptal edilirken de hata:', cancelError);
            }
          }

          // Hata mesajını göster - reader aktif değilse özel mesaj
          const errorMessage = error.response?.data?.error || error.message || 'SumUp ödeme başlatılamadı';

          // Reader aktif değil hatası için özel mesaj
          if (errorMessage.includes('Reader') || errorMessage.includes('reader') || errorMessage.includes('404') || errorMessage.includes('bulunamadı')) {
            toast.error('💳 POS cihazı aktif değil veya bulunamadı. Lütfen cihazı kontrol edin.');
          } else {
            toast.error(`💳 ${errorMessage}`);
          }

          setIsProcessingPayment(false);
          return;
        }
      }

      // KART ve NAKİT ödemeleri: direkt tamamla (SumUp pasif)
      if (paymentType === 'kart' || paymentType === 'nakit') {
        // Kart ve nakit ödemeleri backend'e kendi tipleri ile gönder
        const paymentResponse = await ordersAPI.pay(
          newOrder.id,
          paymentType, // 'kart' veya 'nakit' - doğru tipi gönder
          currentAppliedGiftCode?.id,
          currentGiftCodeDiscount
        );

        console.log(`✅ ${paymentType === 'kart' ? 'Kart' : 'Nakit'} ödemesi tamamlandı`);
        // Devam et normal akışa (aşağıdaki kod bloklarına)
      }



      console.log('✅ Ödeme işlemi tamamlandı, hediye kodu backend\'de kaydedildi');

      // Sadakat kodu kullanımı artık otomatik olarak backend'de yapılıyor
      // Frontend'de manuel kullanım kaldırıldı

      // Bekleyen ödül kullanımını kaydet
      if (pendingLoyaltyReward) {
        try {
          await loyaltyCodesAPI.useReward(
            pendingLoyaltyReward.loyaltyCodeId,
            pendingLoyaltyReward.productId,
            pendingLoyaltyReward.tempLoyaltyCount
          );
          toast.success(`🎁 Ödül kullanıldı!`);

          // Applied loyalty code state'ini güncelle - reward_used_at ekle
          if (appliedLoyaltyCode) {
            setAppliedLoyaltyCode({
              ...appliedLoyaltyCode,
              reward_used_at: new Date().toISOString()
            });
          }

          setPendingLoyaltyReward(null); // Bekleyen ödül kullanımını temizle
        } catch (error) {
          console.error('❌ Ödül kullanım hatası:', error);
          toast.error('Ödül kullanımı kaydedilemedi, ancak ödeme tamamlandı');
        }
      }

      toast.success('Ödeme alındı! Sipariş mutfağa gönderildi ve stok güncellendi.');

      const completedOrderData = await ordersAPI.getById(newOrder.id);

      setCompletedOrder({
        ...completedOrderData,
        payment_type: paymentType,
        completed_at: new Date().toISOString()
      });


      setShowPaymentModal(false);
      setShowReceiptModal(true);
      await clearCart();

      // Hediye kodu state'lerini ve ref'lerini temizle
      setGiftCode('');
      setAppliedGiftCode(null);
      setGiftCodeDiscount(0);
      appliedGiftCodeRef.current = null;
      giftCodeDiscountRef.current = 0;

      // Sadakat kodu state'lerini temizle
      setLoyaltyCode('');
      setAppliedLoyaltyCode(null);
      setTempLoyaltyCount(0); // Geçici sayımı sıfırla
      setPendingLoyaltyReward(null); // Bekleyen ödül kullanımını temizle

      // Ürünleri yeniden yükle (stok güncellemesi için)
      try {
        const allProductsData = await productsAPI.getAll();
        setAllProducts(allProductsData);

        // Eğer kategori seçiliyse, o kategorideki ürünleri filtrele
        if (selectedCategory !== null) {
          const filteredProducts = allProductsData.filter(product =>
            product.category_id === selectedCategory && product.is_active
          );
          setProducts(filteredProducts);
        } else {
          // Tüm aktif ürünleri göster
          const activeProducts = allProductsData.filter(product => product.is_active);
          setProducts(activeProducts);
        }
      } catch (error) {
        // console.error('Ürünler yeniden yüklenirken hata:', error);
      }

    } catch (error: any) {
      console.error('❌ Hızlı ödeme hatası:', error);
      toast.error('Ödeme işlenirken hata oluştu');
    } finally {
      console.log('✅ processQuickPayment tamamlandı, isProcessingPayment sıfırlanıyor');
      setIsWaitingForPosResponse(false);
      setIsProcessingPayment(false);
    }
  };

  const startQrPaymentStatusCheck = (orderId: number) => {
    // Önceki interval'ı temizle
    if (qrPaymentInterval) {
      clearInterval(qrPaymentInterval);
    }

    const checkInterval = setInterval(async () => {
      try {
        const status = await payconiqAPI.getPaymentStatus(orderId);
        setQrPaymentStatus(status.status);

        if (status.status === 'SUCCEEDED') {
          clearInterval(checkInterval);
          setQrPaymentInterval(null);
          setIsQrPaymentActive(false);
          setQrPaymentData(null);
          setCurrentOrderId(null);
          setIsProcessingPayment(false); // Processing state'i temizle
          toast.success('✅ QR ödeme tamamlandı!');

          // Modal'ı kapat
          setShowPaymentModal(false);

          // QR payment'ı müşteri ekranından temizle (sepeti koru)
          try {
            await realtimeCartAPI.clearQRPayment();
            console.log('✅ QR payment müşteri ekranından temizlendi');
          } catch (error) {
            console.error('❌ QR payment temizleme hatası:', error);
          }

          // Backend'de de QR payment'ı temizle
          try {
            await realtimeCartAPI.clearAfterPayment();
            console.log('✅ Backend QR payment temizlendi');
          } catch (error) {
            console.error('❌ Backend QR payment temizleme hatası:', error);
          }

          // Hediye kodu state'lerini ve ref'lerini temizle
          setGiftCode('');
          setAppliedGiftCode(null);
          setGiftCodeDiscount(0);
          appliedGiftCodeRef.current = null;
          giftCodeDiscountRef.current = 0;

          // Sadakat kodu state'lerini temizle
          setLoyaltyCode('');
          setAppliedLoyaltyCode(null);
          setTempLoyaltyCount(0);
          setPendingLoyaltyReward(null);

          // Sepeti temizle (QR ödeme tamamlandığında)
          await clearCart(false); // QR payment'ı da temizle
          console.log('✅ QR ödeme tamamlandı, sepet temizlendi');

          // QR ödeme başarılı olduğunda otomatik fiş sayfası aç
          try {
            const orderData = await ordersAPI.getById(orderId);
            navigate(`/receipt/${orderId}`, {
              state: {
                paymentType: 'qr',
                completedAt: new Date().toISOString()
              }
            });
            console.log('✅ QR ödeme sonrası fiş sayfası açıldı');
          } catch (error) {
            console.error('❌ Fiş sayfası açılamadı:', error);
            toast.error('Fiş sayfası açılamadı');
          }

        } else if (status.status === 'CANCELLED' || status.status === 'EXPIRED' || status.status === 'FAILED' || status.status === 'AUTHORIZATION_FAILED') {
          clearInterval(checkInterval);
          setQrPaymentInterval(null);
          setIsQrPaymentActive(false);
          setQrPaymentData(null);
          setCurrentOrderId(null);
          setIsProcessingPayment(false); // Processing state'i temizle

          // Status'a göre farklı mesajlar
          if (status.status === 'EXPIRED') {
            toast.error('⏰ QR ödeme süresi doldu');
          } else if (status.status === 'CANCELLED') {
            toast.error('🚫 QR ödeme iptal edildi');
          } else {
            toast.error('❌ QR ödeme başarısız');
          }

          // QR payment'ı müşteri ekranından temizle
          try {
            await realtimeCartAPI.clearQRPayment();
            console.log('✅ QR payment iptal edildi, müşteri ekranından temizlendi');
          } catch (error) {
            console.error('❌ QR payment temizleme hatası:', error);
          }
        }
      } catch (error) {
        console.error('QR ödeme durumu kontrol hatası:', error);
      }
    }, 3000); // Her 3 saniyede bir kontrol et

    setQrPaymentInterval(checkInterval);
  };

  const cancelQrPayment = async () => {
    if (!currentOrderId) {
      console.log('❌ QR ödeme iptal edilemiyor: currentOrderId yok');
      toast.error('QR ödeme iptal edilemiyor');
      return;
    }

    console.log('🔄 QR ödeme iptal ediliyor, orderId:', currentOrderId);

    // Frontend'de QR payment state'ini hemen temizle
    setIsQrPaymentActive(false);
    setQrPaymentData(null);
    setQrPaymentStatus('');
    const orderIdToCancel = currentOrderId; // Backup orderId for API call
    setCurrentOrderId(null);
    setQrPaymentCancelledByUser(true); // Kullanıcı tarafından iptal edildiğini işaretle
    setIsProcessingPayment(false); // Processing state'i temizle

    // QR ödeme interval'ını temizle
    if (qrPaymentInterval) {
      clearInterval(qrPaymentInterval);
      setQrPaymentInterval(null);
    }

    // Backend'de QR ödemeyi iptal etmeye çalış (yetkisi varsa)
    if (user && ['admin', 'garson', 'mutfak'].includes(user.role)) {
      try {
        // CSRF token'ı yenile
        // CSRF token yenileniyor
        await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/csrf-token`, {
          credentials: 'include'
        });

        // Backend QR ödeme iptal ediliyor
        await payconiqAPI.cancelPayment(orderIdToCancel);
        // Backend QR ödeme iptal edildi
        toast.success('🚫 QR ödeme iptal edildi');
      } catch (error: any) {
        // QR ödeme iptal hatası
        toast.success('🚫 QR ödeme iptal edildi (backend bağlantı hatası)');
      }
    } else {
      toast.success('🚫 QR ödeme iptal edildi');
    }

    // Müşteri ekranından SADECE QR payment'ı temizle (sepeti koruyalım)
    // Sadece admin/garson/mutfak rolleri için backend çağrısı yap
    if (user && ['admin', 'garson', 'mutfak'].includes(user.role)) {
      try {
        console.log('🔄 Müşteri ekranından QR payment temizleniyor...');
        await realtimeCartAPI.clearQRPayment();
        console.log('✅ Müşteri ekranından QR payment temizlendi');
      } catch (error) {
        console.error('❌ Müşteri ekranı QR payment temizleme hatası:', error);
      }
    } else {
      console.log('ℹ️ Customer: QR payment frontend\'de temizlendi (backend çağrısı yapılmadı)');
    }
  };

  const printReceipt = () => {
    if (!completedOrder) return;

    // Standart fiş yazdırma fonksiyonunu kullan
    printStandardReceipt(completedOrder, receiptSettings, formatPrice);
    toast.success('Fiş yazdırılıyor...');
  };

  const finishOrder = async () => {
    // İşlem tamamlandığında sepeti temizle ve müşteri ekranını güncelle
    try {
      // clearCart fonksiyonu zaten hem frontend'i hem backend'i temizleyecek
      await clearCart(false); // QR payment'ı da temizle çünkü işlem tamamlandı
      setQrPaymentCancelledByUser(false); // Flag'i sıfırla

      console.log('✅ İşlem tamamlandı, sepet temizlendi ve müşteri ekranı güncellendi');

      setShowReceiptModal(false);
      setCompletedOrder(null);
      setWantsReceipt(false); // Fiş isteme durumunu sıfırla
      toast.success('İşlem tamamlandı!');
    } catch (error) {
      console.error('❌ İşlem tamamlama hatası:', error);
      // Modal'ı kapat ama hata mesajı göster
      setShowReceiptModal(false);
      setCompletedOrder(null);
      setWantsReceipt(false);
      toast.error('İşlem tamamlanırken hata oluştu');
    }
  };

  // Müşteri ekranı toggle fonksiyonu kaldırıldı - artık hep açık

  // Gerçek zamanlı sepet güncellemesi (mevcut cart state'ini kullanır)
  const updateRealtimeCart = async () => {
    // Eğer kullanıcı QR iptal ettiyse, QR state'ini koruma
    const preserveQr = !qrPaymentCancelledByUser;
    updateRealtimeCartWithData(cart, true, preserveQr); // Müşteri ekranı hep açık
  };

  // Gerçek zamanlı sepet güncellemesi (belirli cart data'sını kullanır)
  const updateRealtimeCartWithData = async (cartData: typeof cart, forceEnabled?: boolean, preserveQrPayment = true) => {
    const isEnabled = true; // Müşteri ekranı hep açık

    // Admin/garson/mutfak rolleri için normal güncelleme
    if (user && ['admin', 'garson', 'mutfak'].includes(user.role)) {
      // Normal realtime cart update
    } else {
      // Customer rolü için backend çağrısı yapma
      console.log('ℹ️ Customer: Realtime cart güncellemesi atlandı (backend çağrısı yapılmadı)');
      return; // Customer için normal sepet güncellemesi yapma
    }

    if (!isEnabled) {
      return;
    }

    // Gerçek toplam hesaplama (indirimler dahil) - Fiyata Dahil Mod
    const subtotal = cartData.reduce((total, item) => {
      if (item.notes === '🎁 ÖDÜL') return total;
      return total + (item.product.price * item.quantity);
    }, 0);

    const taxTotal = cartData.reduce((total, item) => {
      if (item.notes === '🎁 ÖDÜL') return total;
      const itemTotal = item.product.price * item.quantity;
      const taxRate = item.product.tax_rate || 0;
      const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
      return total + taxAmount;
    }, 0);

    const finalTotal = subtotal; // Fiyatlar zaten vergili
    const discountedTotal = Math.max(0, finalTotal - giftCodeDiscount);

    const totals = {
      subtotal,
      tax: taxTotal,
      total: discountedTotal,
      discount: giftCodeDiscount
    };

    const payload = {
      items: cartData.map(item => ({
        id: item.product.id,
        product_id: item.product.id, // Backend için product_id ekle
        product_name: item.product.name,
        quantity: item.quantity,
        unit_price: item.product.price,
        notes: item.notes,
        is_reward: item.notes === '🎁 ÖDÜL'
      })),
      totals,
      enabled: isEnabled,
      coupons: {
        giftCode: appliedGiftCode ? {
          code: appliedGiftCode.code,
          discount: appliedGiftCode.discount || 0,
          type: appliedGiftCode.type
        } : null,
        loyaltyCode: appliedLoyaltyCode ? {
          code: appliedLoyaltyCode.code,
          points: appliedLoyaltyCode.points,
          reward: appliedLoyaltyCode.reward,
          currentCount: appliedLoyaltyCode.current_count + (tempLoyaltyCount || 0), // Geçici sayım dahil
          requiredQuantity: appliedLoyaltyCode.required_quantity,
          isCompleted: appliedLoyaltyCode.is_completed || (appliedLoyaltyCode.current_count + (tempLoyaltyCount || 0)) >= appliedLoyaltyCode.required_quantity,
          rewardUsedAt: appliedLoyaltyCode.reward_used_at
        } : null
      },
      // QR payment verisini koru - sepet güncellemelerinde kaybolmasın
      // Ama kullanıcı iptal ettiyse gönderme
      preserveQrPayment: preserveQrPayment && !qrPaymentCancelledByUser
    };

    try {
      // Debug: Gönderilen veriyi kontrol et
      console.log('🔍 QR State Debug:', {
        qrPaymentCancelledByUser,
        preserveQrPayment,
        hasQrPayment: !!payload.preserveQrPayment,
        cartLength: cartData.length
      });

      // Sadece admin/garson/mutfak rolleri için backend çağrısı yap
      if (user && ['admin', 'garson', 'mutfak'].includes(user.role)) {
        await realtimeCartAPI.update(payload);
      } else {
        console.log('ℹ️ Customer: Realtime cart update atlandı (backend çağrısı yapılmadı)');
      }
    } catch (error: any) {
      console.error('❌ Gerçek zamanlı sepet güncelleme hatası:', error);
      // API interceptor zaten token yenilemeyi halledecek, burada ekstra işlem yapmaya gerek yok
    }
  };

  // Admin panel yenilendiğinde sepet durumunu kontrol et
  const handleAdminRefreshCartState = async (products: Product[]) => {
    try {
      console.log('🔄 Admin panel yenilendi, sepet durumu kontrol ediliyor...');
      const response = await realtimeCartAPI.getAdminState();

      if (response.success && response.cart && response.cart.items && response.cart.items.length > 0) {
        // Backend'de sepet var, otomatik temizle
        console.log(`🧹 Admin panel yenilendi, ${response.cart.items.length} ürünlü sepet otomatik temizleniyor...`);
        await clearBackendCartState();
        console.log('✅ Admin panel yenilendi, sepet otomatik temizlendi');
        toast.success('Admin panel yenilendi - sepet temizlendi, yeni siparişe başlayabilirsiniz');
      } else {
        console.log('ℹ️ Admin panel yenilendi, backend\'de sepet yok');
      }
    } catch (error) {
      console.error('❌ Admin refresh cart state hatası:', error);
      // Hata durumunda sessizce devam et
    }
  };

  // Backend sepet state'ini temizle (admin refresh için)
  const clearBackendCartState = async () => {
    try {
      console.log('🧹 Backend sepet state temizleniyor...');

      // Frontend cart'ı temizle
      setCart([]);
      setAppliedGiftCode(null);
      setGiftCode('');
      setGiftCodeDiscount(0);
      appliedGiftCodeRef.current = null;
      giftCodeDiscountRef.current = 0;
      setAppliedLoyaltyCode(null);
      setLoyaltyCode('');
      setTempLoyaltyCount(0);
      setPendingLoyaltyReward(null);
      setQrPaymentCancelledByUser(false);

      // QR payment state'ini temizle
      setIsQrPaymentActive(false);
      setQrPaymentData(null);
      setQrPaymentStatus('');
      setCurrentOrderId(null);

      // Backend sepet state'ini de temizle (müşteri ekranı da boşalacak)
      await updateRealtimeCartWithData([], true, false);

      console.log('✅ Tüm sepet state\'i temizlendi (frontend + backend + müşteri ekranı)');
    } catch (error) {
      console.error('❌ Backend sepet temizleme hatası:', error);
      toast.error('Sepet temizlenirken hata oluştu');
    }
  };

  // Sayfa yenileme sonrası backend'den cart state'ini yükle
  const loadBackendCartState = async (products: Product[]) => {
    try {
      console.log('🔄 Backend cart state yükleniyor...');
      const response = await realtimeCartAPI.getAdminState();

      if (response.success && response.cart) {
        const backendCart = response.cart;
        console.log('📦 Backend cart state:', backendCart);

        // Backend'de items varsa, frontend cart'ını güncelle
        if (backendCart.items && backendCart.items.length > 0) {
          // Zaten yüklenmiş product verilerini kullan (optimizasyon)
          const productMap = new Map(products.map((p: Product) => [p.id, p]));

          // Backend items'ı frontend cart formatına çevir
          const frontendCart = backendCart.items.map((item: any, index: number) => {
            const product = productMap.get(item.id);
            if (!product) {
              console.warn('⚠️ Product bulunamadı:', item.id);
              return null;
            }

            return {
              id: Date.now() + index, // Unique ID
              product: product,
              quantity: item.quantity,
              notes: item.notes || '',
              timestamp: Date.now()
            };
          }).filter(Boolean); // null'ları filtrele

          if (frontendCart.length > 0) {
            setCart(frontendCart as typeof cart);
            console.log('✅ Frontend cart backend\'den senkronize edildi:', frontendCart.length, 'item');
            // Toast sadece gerçekten item varsa göster (gereksiz bildirimi azalt)
            if (frontendCart.length > 1) {
              toast.success(`Sepet durumu yüklendi (${frontendCart.length} ürün)`);
            }
          }
        }

        // QR payment state'ini senkronize et
        if (backendCart.qrPayment) {
          setQrPaymentData(backendCart.qrPayment);
          setQrPaymentStatus(backendCart.qrPayment.status || 'PENDING');
          setIsQrPaymentActive(true);
          setCurrentOrderId(backendCart.qrPayment.orderId);
          console.log('✅ QR payment state senkronize edildi');
        }

        // Coupon state'ini senkronize et
        if (backendCart.coupons) {
          if (backendCart.coupons.giftCode) {
            setAppliedGiftCode(backendCart.coupons.giftCode);
            setGiftCodeDiscount(backendCart.coupons.giftCode.discount || 0);
          }
          if (backendCart.coupons.loyaltyCode) {
            setAppliedLoyaltyCode(backendCart.coupons.loyaltyCode);
          }
        }
      } else {
        console.log('ℹ️ Backend\'de cart state yok, boş sepet ile başlanıyor');
      }
    } catch (error) {
      console.error('❌ Backend cart state yükleme hatası:', error);
      // Hata durumunda sessizce devam et, kullanıcıya hata gösterme
    }
  };


  // loadCustomerScreenStatus fonksiyonu kaldırıldı - müşteri ekranı hep açık

  // Fiş ayarlarını yükle
  const loadReceiptSettings = async () => {
    try {
      const settings = await settingsAPI.getReceiptSettings();
      setReceiptSettings({
        receiptTitle: settings.receiptTitle || '',
        receiptSubtitle: settings.receiptSubtitle || '',
        receiptPhone: settings.receiptPhone || '',
        receiptTaxNumber: settings.receiptTaxNumber || '',
        receiptFooter: settings.receiptFooter || '',
        receiptDateLabel: settings.receiptDateLabel || '',
        receiptTimeLabel: settings.receiptTimeLabel || '',
        receiptOrderNumberLabel: settings.receiptOrderNumberLabel || '',
        receiptOrderDetailsLabel: settings.receiptOrderDetailsLabel || '',
        receiptPaymentLabel: settings.receiptPaymentLabel || '',
        receiptPaymentCash: settings.receiptPaymentCash || '',
        receiptPaymentCard: settings.receiptPaymentCard || '',
        receiptSubtotalLabel: settings.receiptSubtotalLabel || '',
        receiptTaxLabel: settings.receiptTaxLabel || '',
        receiptTotalLabel: settings.receiptTotalLabel || '',
        receiptQuantityLabel: settings.receiptQuantityLabel || '',
        receiptShowTaxDetails: settings.receiptShowTaxDetails || false,
        receiptShowPaymentType: settings.receiptShowPaymentType || false,
        receiptShowOrderNumber: settings.receiptShowOrderNumber || false,
        receiptShowDateTime: settings.receiptShowDateTime || false
      });
    } catch (error) {
      // console.error('Fiş ayarları yüklenemedi:', error);
    }
  };

  // Saat güncelleme
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const categoriesData = await productsAPI.getCategories();
        setCategories(categoriesData);

        const allProductsData = await productsAPI.getAll();

        const activeProducts = allProductsData.filter((p: Product) => p.is_active);
        setAllProducts(activeProducts);

        // Tüm ürünleri göster (kategori filtresi olmadan)
        setSelectedCategory(null);

        // Admin panel yenilendiğinde sepet durumunu kontrol et
        await handleAdminRefreshCartState(activeProducts);
      } catch (error) {
        toast.error('Veriler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    loadReceiptSettings(); // Fiş ayarlarını yükle
  }, []);

  useEffect(() => {
    let filtered = allProducts;

    if (selectedCategory) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setProducts(filtered);
  }, [selectedCategory, searchTerm, allProducts]);

  // Hediye kodu ve sadakat kodu değişikliklerini dinle ve müşteri ekranını güncelle
  useEffect(() => {
    console.log('🔄 useEffect tetiklendi:', {
      appliedLoyaltyCode: appliedLoyaltyCode?.code || null,
      tempLoyaltyCount,
      cartLength: cart.length
    });

    if (cart.length > 0) {
      // Hediye kodu ve sadakat kodu state'leri değiştiğinde müşteri ekranını güncelle
      // QR iptal edilmişse QR state'ini koruma
      const preserveQr = !qrPaymentCancelledByUser;
      updateRealtimeCartWithData(cart, true, preserveQr);
    } else if (cart.length === 0 && qrPaymentCancelledByUser) {
      // Sepet boş ve QR iptal edilmişse, QR state'ini temizle
      updateRealtimeCartWithData([], true, false);
    }
  }, [appliedGiftCode, giftCodeDiscount, appliedLoyaltyCode, tempLoyaltyCount, cart, qrPaymentCancelledByUser]);

  // Stok bildirimi - saatte 1 defa göster (sadece admin için)
  useEffect(() => {
    // Sadece admin kullanıcıları için çalışsın
    if (user?.role !== 'admin') {
      return;
    }

    const checkLowStock = async () => {
      try {
        // Son bildirim zamanını kontrol et (localStorage'da)
        const lastNotificationKey = 'quickOrderLowStockNotificationLastShown';
        const lastNotificationTime = localStorage.getItem(lastNotificationKey);
        const now = new Date();
        const currentHour = now.getHours();

        // Eğer son bildirim bu saatte gösterildiyse, tekrar gösterme
        if (lastNotificationTime) {
          const lastHour = new Date(lastNotificationTime).getHours();
          if (lastHour === currentHour) {
            return; // Aynı saat içinde bildirim gösterilmiş, tekrar gösterme
          }
        }

        // Azalan stoklu ürünleri getir
        const lowStockProducts = await productsAPI.getLowStock();

        // Eğer azalan stoklu ürün varsa bildirim göster
        if (lowStockProducts && lowStockProducts.length > 0) {
          setLowStockNotification({
            products: lowStockProducts.slice(0, 3), // İlk 3 ürün
            show: true,
          });

          // Son bildirim zamanını kaydet
          localStorage.setItem(lastNotificationKey, now.toISOString());

          // 10 saniye sonra otomatik kapat
          if (lowStockTimeoutRef.current) {
            clearTimeout(lowStockTimeoutRef.current);
          }
          lowStockTimeoutRef.current = setTimeout(() => {
            setLowStockNotification(prev => prev ? { ...prev, show: false } : null);
          }, 10000); // 10 saniye
        }
      } catch (error) {
        // Hata durumunda sessizce geç
        console.error('❌ Stok kontrolü hatası:', error);
      }
    };

    // İlk yüklemede kontrol et (5 saniye bekle - sayfa yüklenirken hemen gösterme)
    const initialTimeout = setTimeout(() => {
      checkLowStock();
    }, 5000); // 5 saniye sonra ilk kontrol

    // Her saatte bir kontrol et (60 dakika = 3600000 ms)
    const interval = setInterval(() => {
      checkLowStock();
    }, 3600000); // 1 saat = 3600000 ms

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      if (lowStockTimeoutRef.current) {
        clearTimeout(lowStockTimeoutRef.current);
      }
    };
  }, [user?.role]); // user role değiştiğinde yeniden başlat

  if (loading) {
    return (
      <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg transition-colors duration-500">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown"></div>
          <span className="ml-2 text-cafe-brown dark:text-cafe-light">Hızlı Sipariş</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg transition-colors duration-500">
      {/* Stok Bildirimi - Üstten görünen (fixed, sayfayı itmez) */}
      {lowStockNotification && lowStockNotification.show && lowStockNotification.products.length > 0 && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-orange-500 dark:bg-orange-700 border-b-2 border-orange-600 dark:border-orange-800 shadow-lg animate-slideDown">
          <div className="container mx-auto px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-lg flex-shrink-0">⚠️</span>
                <div className="flex-1 min-w-0">
                  <span className="font-bold text-white text-sm mr-2">Stok Uyarısı:</span>
                  <span className="text-white text-sm">
                    {lowStockNotification.products.map((product, index) => (
                      <span key={product.id}>
                        {index > 0 && ', '}
                        <span className="font-semibold">{product.name}</span>
                        <span className="text-orange-100"> ({product.stock_quantity})</span>
                      </span>
                    ))}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  setLowStockNotification(prev => prev ? { ...prev, show: false } : null);
                  if (lowStockTimeoutRef.current) {
                    clearTimeout(lowStockTimeoutRef.current);
                  }
                }}
                className="flex-shrink-0 text-white hover:text-orange-100 hover:bg-orange-600 dark:hover:bg-orange-800 rounded-full p-1 transition-colors"
                aria-label="Bildirimi kapat"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header - Normal akışta */}
      <div className="bg-white dark:bg-dark-surface shadow-lg border-b border-gray-200 dark:border-dark-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-cafe-brown dark:text-cafe-light">
                {pageTitle}
              </h1>
              <p className="text-sm text-gray-600 dark:text-dark-text mt-1">
                Hoş geldiniz, {user?.username} ({user?.role})
              </p>
            </div>

            {/* Orta kısım - Saat */}
            <div className="hidden lg:flex flex-col items-center justify-center flex-1 mx-8">
              <div className="text-2xl text-cafe-brown dark:text-cafe-light font-bold">
                🕐 {getCurrentTimeBelgiumString().split(' ')[1]}
              </div>
              <div className="text-sm text-gray-600 dark:text-dark-text">
                {getCurrentTimeBelgiumString().split(' ')[0]}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={toggleTheme}
                title="Tema değiştir"
              >
                {isDark ? '☀️' : '🌙'}
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={() => navigate('/orders')}
                className="text-xs sm:text-sm"
              >
                📋 Siparişler
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={() => navigate('/tables')}
                className="text-xs sm:text-sm"
              >
                🍽️ Masalar
              </Button>
              {user?.role === 'admin' && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="text-xs sm:text-sm"
                >
                  ⚙️ Admin
                </Button>
              )}
              <Button
                variant="danger"
                size="sm"
                onClick={logout}
                className="text-xs sm:text-sm"
              >
                🚪 Çıkış
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Ana içerik */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* Sol Panel - Kategoriler */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-4 border border-gray-100 dark:border-dark-border">
              <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-dark-text">
                📁 Kategoriler
              </h3>

              {/* Arama */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Ürün Ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-cafe-brown focus:border-cafe-brown dark:bg-dark-bg dark:text-dark-text text-sm"
                />
              </div>

              {/* Kategori Listesi */}
              <div className="space-y-2">
                <Button
                  variant={selectedCategory === null ? 'primary' : 'secondary'}
                  size="sm"
                  fullWidth
                  onClick={() => setSelectedCategory(null)}
                  className="justify-start text-left"
                >
                  📦 Tümü
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'primary' : 'secondary'}
                    size="sm"
                    fullWidth
                    onClick={() => setSelectedCategory(category.id)}
                    className="justify-start text-left"
                  >
                    📁 {category.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Orta Panel - Ürünler */}
          <div className="lg:col-span-7">
            <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6 border border-gray-100 dark:border-dark-border">
              <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-dark-text">
                🍽️ Ürünler
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-[600px] overflow-y-auto">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border-2 border-gray-300 dark:border-gray-600 overflow-hidden cursor-pointer hover:shadow-md hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-150"
                    onClick={() => addToCart(product)}
                    title={`${product.name} - ${formatPrice(product.price)}`}
                  >
                    {/* Ürün Görseli */}
                    <div className="relative h-24 bg-gray-100 dark:bg-gray-700">
                      {product.image_url ? (
                        <img
                          src={product.image_url.startsWith('http') ? product.image_url : `${process.env.REACT_APP_API_URL || window.location.origin}${product.image_url}`}
                          alt={product.name}
                          className="w-full h-full object-cover rounded-t-lg"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400 dark:text-gray-500"><span class="text-lg">🖼️</span></div>';
                          }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
                          <span className="text-2xl">🍽️</span>
                        </div>
                      )}

                      {/* Stok Bilgisi - Sağ Üst Köşe */}
                      {product.stock_quantity !== null && product.stock_quantity !== undefined && (
                        <div className="absolute top-1 right-1">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium bg-white/90 backdrop-blur-sm ${product.stock_quantity > 0
                            ? 'text-green-800'
                            : 'text-red-800'
                            }`}>
                            S: {product.stock_quantity > 0 ? product.stock_quantity : 'Yok'}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-2">
                      <h3 className="font-medium text-gray-900 dark:text-dark-text mb-1 text-sm" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {product.name}
                      </h3>



                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-base font-bold text-cafe-brown dark:text-cafe-light">
                            {formatPrice(product.price)}
                          </span>
                        </div>

                        {/* Not Ekleme Butonu */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Ana kartın tıklama olayını engelle
                            openNoteModal(product);
                          }}
                          className="bg-blue-500 text-white p-1.5 rounded text-xs hover:bg-blue-600 transition-colors"
                          title="Tema değiştir"
                        >
                          📝
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {products.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">
                    {searchTerm ? 'Arama kriterinize uygun ürün bulunamadı' : 'Bu kategoride ürün bulunmuyor'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    {searchTerm ? 'Farklı arama terimleri deneyin' : 'Başka bir kategori seçin'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sağ Panel - Sepet */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6 border border-gray-100 dark:border-dark-border sticky top-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-dark-text flex items-center">
                🛒 Sepet ({cart.length} ürün)
              </h3>

              {cart.length === 0 ? (
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                    Sepet boş
                  </p>

                  {/* Sadakat Kodu Input'u - Sadece sepet boşken görünür */}
                  <div className="p-4 bg-gray-50 dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-dark-border">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
                      🎁 Sadakat Kodu Sorgula
                    </h4>
                    <div className="flex gap-2 w-full">
                      <input
                        type="text"
                        value={loyaltyCode}
                        onChange={(e) => setLoyaltyCode(e.target.value.toUpperCase())}
                        placeholder="A7B2"
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text text-sm focus:ring-2 focus:ring-cafe-brown focus:border-cafe-brown"
                      />
                      <button
                        onClick={validateLoyaltyCode}
                        disabled={isValidatingLoyaltyCode || !loyaltyCode.trim()}
                        className="px-4 py-2 bg-cafe-brown text-white rounded-lg hover:bg-cafe-dark disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap"
                      >
                        {isValidatingLoyaltyCode ? '⏳' : '🔍'}
                      </button>
                    </div>

                    {/* Sadakat Kodu Durumu */}
                    {appliedLoyaltyCode && (
                      <div className={`mt-3 p-3 rounded-lg border ${appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : appliedLoyaltyCode.is_completed && appliedLoyaltyCode.reward_used_at
                          ? 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
                          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                              🎯 {appliedLoyaltyCode.code}
                            </p>
                            <p className={`text-xs ${appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at
                              ? 'text-green-600 dark:text-green-300'
                              : appliedLoyaltyCode.is_completed && appliedLoyaltyCode.reward_used_at
                                ? 'text-gray-600 dark:text-gray-300'
                                : 'text-red-600 dark:text-red-300'
                              }`}>
                              {appliedLoyaltyCode.current_count}/{appliedLoyaltyCode.required_quantity} tamamlandı
                            </p>
                            {appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at && (
                              <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                                🎉 Ödül hazır! {appliedLoyaltyCode.reward_product_name}
                              </p>
                            )}
                            {appliedLoyaltyCode.is_completed && appliedLoyaltyCode.reward_used_at && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                                ✅ Ödül kullanıldı
                              </p>
                            )}
                            {!appliedLoyaltyCode.is_completed && (
                              <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                                ❌ Henüz tamamlanmadı! Daha fazla alışveriş yapın.
                              </p>
                            )}
                          </div>
                          {appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at && !pendingLoyaltyReward && (
                            <button
                              onClick={useLoyaltyReward}
                              className="px-3 py-1 bg-green-500 text-white rounded text-xs font-medium hover:bg-green-600 transition-colors"
                            >
                              🎁 Ödülü Kullan
                            </button>
                          )}
                          {pendingLoyaltyReward && (
                            <button
                              disabled
                              className="px-3 py-1 bg-gray-400 text-white rounded text-xs font-medium cursor-not-allowed"
                            >
                              ✅ Ödül Beklemede
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                    {cart.map((item, index) => (
                      <div key={index} className="flex justify-between items-center border-b border-gray-200 dark:border-dark-border pb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-800 dark:text-dark-text">
                            {item.product?.name}
                            {item.notes === '🎁 ÖDÜL' && (
                              <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">
                                ÖDÜL
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {item.notes === '🎁 ÖDÜL' ? (
                              <span className="text-green-600 dark:text-green-400 font-medium">
                                ÖDÜL × {item.quantity}
                              </span>
                            ) : (
                              `${formatPrice(item.product.price)} × ${item.quantity}`
                            )}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateCartItemQuantity(index, item.quantity - 1)}
                            className="w-8 h-8 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                          >
                            -
                          </button>
                          <span className="w-8 text-center font-medium text-gray-800 dark:text-dark-text">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateCartItemQuantity(index, item.quantity + 1)}
                            className="w-8 h-8 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 dark:border-dark-border pt-4">
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Ara Toplam:</span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {formatPrice(calculateTotal())}
                        </span>
                      </div>
                      {calculateTax() > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">KDV:</span>
                          <span className="text-gray-700 dark:text-gray-300">
                            {formatPrice(calculateTax())}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center border-t border-gray-200 dark:border-dark-border pt-2">
                        <span className="text-lg font-semibold text-gray-800 dark:text-dark-text">Genel Toplam:</span>
                        <span className="text-2xl font-bold text-cafe-brown dark:text-cafe-light">
                          {formatPrice(calculateGrandTotal())}
                        </span>
                      </div>
                    </div>


                    <div className="space-y-3">
                      {user?.can_process_payments || user?.role === 'admin' ? (
                        <button
                          onClick={startPaymentProcess}
                          className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 font-semibold"
                        >
                          💳 Ödeme Yap
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full bg-gray-400 text-white py-3 rounded-lg cursor-not-allowed font-semibold"
                          title="Tema değiştir"
                        >
                          🚫 Ödeme İzni Yok
                        </button>
                      )}

                      <button
                        onClick={async () => await clearCart()}
                        className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-semibold"
                      >
                        🗑️ Sepeti Temizle
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ödeme Modalı */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          // Modal kapatıldığında tüm state'leri temizle
          setIsProcessingPayment(false); // 🔥 KRİTİK: İşlem state'ini temizle
          setCustomerPaid(''); // Para üstü input'unu sıfırla

          // QR ödeme interval'ını temizle (eğer varsa)
          if (qrPaymentInterval) {
            clearInterval(qrPaymentInterval);
            setQrPaymentInterval(null);
          }

          setLoyaltyCode('');
          setAppliedLoyaltyCode(null);
          setTempLoyaltyCount(0);
          setPendingLoyaltyReward(null);
        }}
        title="Ödeme"
        size="md"
        closeOnOverlayClick={false}
      >

        {/* Sipariş Özeti */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-800 dark:text-white mb-3">
            🛒 Sipariş Özeti
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {cart.map((item, index) => (
              <div key={index} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 dark:text-white">
                      {item.product?.name}
                      {item.notes === '🎁 ÖDÜL' && (
                        <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">
                          ÖDÜL
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {item.notes === '🎁 ÖDÜL' ? (
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          {item.quantity} x ÖDÜL
                        </span>
                      ) : (
                        `${item.quantity} x ${formatPrice(item.product?.price || 0)}`
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-800 dark:text-white">
                      {item.notes === '🎁 ÖDÜL' ? (
                        <span className="text-green-600 dark:text-green-400">
                          ÖDÜL <span className="line-through text-gray-400 ml-2">{formatPrice(item.quantity * (item.product?.price || 0))}</span>
                        </span>
                      ) : (
                        formatPrice(item.quantity * (item.product?.price || 0))
                      )}
                    </div>
                    {/* Ürün bazında KDV gösterilmez - sadece toplamda hesaplanır */}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Toplam ve Vergi Hesaplaması */}
          <div className="border-t border-gray-300 dark:border-gray-600 pt-3 mt-3 space-y-2">
            {(() => {
              const subtotal = cart.reduce((total, item) => {
                // Ödül ürünleri ücretsiz
                if (item.notes === '🎁 ÖDÜL') {
                  return total;
                }
                return total + ((item.product?.price || 0) * item.quantity);
              }, 0);

              // KDV hesaplama - calculateTax() ile aynı hesaplama kullan
              const taxTotal = calculateTax();
              let taxInfo = '';

              if (taxTotal > 0) {
                // Ürün bazlı vergi oranlarını kontrol et
                const uniqueTaxRates = new Set(cart.map(item => item.product?.tax_rate || 0).filter(rate => rate > 0));

                if (uniqueTaxRates.size === 1) {
                  const rate = Array.from(uniqueTaxRates)[0];
                  taxInfo = `KDV (%${rate})`;
                } else if (uniqueTaxRates.size > 1) {
                  taxInfo = 'KDV (Farklı oranlar)';
                } else {
                  taxInfo = 'KDV';
                }
              } else {
                taxInfo = 'KDV yok';
              }

              const finalTotal = subtotal; // Fiyatlar zaten vergili

              return (
                <>
                  <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                    <span>Ara Toplam:</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  {taxTotal > 0 && (
                    <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                      <div>
                        <span>Vergi:</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{taxInfo}</div>
                      </div>
                      <span>{formatPrice(taxTotal)}</span>
                    </div>
                  )}
                  {giftCodeDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                      <div>
                        <span>🎁 Hediye Kodu İndirimi:</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{appliedGiftCode?.code}</div>
                      </div>
                      <span>-{formatPrice(giftCodeDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold text-gray-800 dark:text-white border-t border-gray-300 dark:border-gray-600 pt-2">
                    <span>TOPLAM:</span>
                    <span className="text-cafe-brown dark:text-cafe-light">{formatPrice(Math.max(0, finalTotal - giftCodeDiscount))}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Hediye Kodu Bölümü */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-3">
            🎁 Hediye Kodu
          </h4>

          {!appliedGiftCode ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={giftCode}
                onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                placeholder="Hediye kodu girin..."
                className="flex-1 border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={validateGiftCode}
                disabled={!giftCode.trim() || isValidatingCode}
              >
                {isValidatingCode ? '⏳' : '✅'} Uygula
              </Button>
            </div>
          ) : (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-green-700 dark:text-green-300 font-medium">
                    ✅ {appliedGiftCode.code} uygulandı
                  </span>
                  <div className="text-sm text-green-600 dark:text-green-400">
                    {formatPrice(giftCodeDiscount)} indirim
                  </div>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={removeGiftCode}
                >
                  ❌ Kaldır
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sadakat Kodu Bölümü */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-3">
            🎴 Sadakat Kodu
          </h4>

          {!appliedLoyaltyCode ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={loyaltyCode}
                onChange={(e) => setLoyaltyCode(e.target.value.toUpperCase())}
                placeholder="Sadakat kodu girin..."
                className="flex-1 border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={validateLoyaltyCode}
                disabled={!loyaltyCode.trim() || isValidatingLoyaltyCode}
              >
                {isValidatingLoyaltyCode ? '⏳' : '✅'} Kontrol Et
              </Button>
            </div>
          ) : (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">
                    🎴 {appliedLoyaltyCode.code}
                  </span>
                  <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                    {appliedLoyaltyCode.is_completed ? (
                      appliedLoyaltyCode.reward_used_at ? (
                        '❌ Ödül zaten kullanılmış'
                      ) : (
                        '🎉 Ödül kullanılabilir!'
                      )
                    ) : (
                      `📊 İlerleme: ${appliedLoyaltyCode.current_count + tempLoyaltyCount}/${appliedLoyaltyCode.required_quantity}`
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {((appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at && !pendingLoyaltyReward) ||
                    (tempLoyaltyCount > 0 && (appliedLoyaltyCode.current_count + tempLoyaltyCount) >= appliedLoyaltyCode.required_quantity && !appliedLoyaltyCode.reward_used_at && !pendingLoyaltyReward)) ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={useLoyaltyReward}
                    >
                      🎁 Ödülü Kullan
                    </Button>
                  ) : pendingLoyaltyReward ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled
                    >
                      ✅ Ödül Beklemede
                    </Button>
                  ) : null}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={removeLoyaltyCode}
                  >
                    ❌ Kaldır
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Ödeme Türü Seçimi */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-3">
            Ödeme Türü Seçin
          </h4>
          <div className="grid grid-cols-4 gap-3">
            <button
              onClick={() => {
                setPaymentType('nakit');
                setWantsReceipt(false); // Nakit seçildiğinde fiş isteme durumunu sıfırla
              }}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${paymentType === 'nakit'
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'border-gray-300 dark:border-dark-border hover:border-gray-400'
                }`}
            >
              <div className="text-2xl mb-2">💵</div>
              <div className="font-semibold text-gray-800 dark:text-dark-text">Nakit</div>
            </button>
            <button
              onClick={() => {
                setPaymentType('kart');
                setWantsReceipt(false); // Kart seçildiğinde fiş isteme durumunu sıfırla
              }}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${paymentType === 'kart'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-gray-300 dark:border-dark-border hover:border-gray-400'
                }`}
            >
              <div className="text-2xl mb-2">💳</div>
              <div className="font-semibold text-gray-800 dark:text-dark-text">Kart</div>
            </button>
            <button
              onClick={() => {
                setPaymentType('sumup');
                setWantsReceipt(false);
              }}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${paymentType === 'sumup'
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                : 'border-gray-300 dark:border-dark-border hover:border-gray-400'
                }`}
            >
              <div className="text-2xl mb-2">💳</div>
              <div className="font-semibold text-gray-800 dark:text-dark-text">SumUp ile Öde</div>
            </button>
            <button
              onClick={() => {
                console.log('QR butonuna tıklandı - Ödeme türü seçimi!');
                setPaymentType('qr');
                setWantsReceipt(false);
              }}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${paymentType === 'qr'
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                : 'border-gray-300 dark:border-dark-border hover:border-gray-400'
                }`}
            >
              <div className="text-2xl mb-2">📱</div>
              <div className="font-semibold text-gray-800 dark:text-dark-text">QR</div>
            </button>
          </div>
        </div>

        {/* Fiş İsteme Onay Kutusu - Sadece Nakit Ödemede */}
        {paymentType === 'nakit' && (
          <>
            <div className="mb-6">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantsReceipt}
                  onChange={(e) => setWantsReceipt(e.target.checked)}
                  className="w-4 h-4 text-cafe-brown bg-gray-100 border-gray-300 rounded focus:ring-cafe-brown focus:ring-2"
                />
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  🧾 Fiş istedi
                </span>
              </label>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Müşteri fiş istiyorsa bu kutuyu işaretleyin
              </p>
            </div>

            {/* Para Üstü Hesaplama */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <label className={`text-sm font-medium whitespace-nowrap ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Müşteri Verdi:
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={customerPaid}
                  onChange={(e) => setCustomerPaid(e.target.value)}
                  placeholder="0.00"
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm ${isDark
                    ? 'bg-dark-surface border-dark-border text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
                <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>€</span>
                {customerPaid && parseFloat(customerPaid) > 0 && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isDark
                    ? 'bg-green-900/30 border-green-700'
                    : 'bg-green-50 border-green-200'
                    }`}>
                    <span className={`text-sm font-medium ${isDark ? 'text-green-300' : 'text-green-700'}`}>Para Üstü:</span>
                    <span className={`text-sm font-bold ${isDark ? 'text-green-200' : 'text-green-800'}`}>{formatPrice(calculateChange())}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Butonlar */}
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={async () => {
              // Eğer QR ödeme aktifse, önce iptal et
              if (isQrPaymentActive && currentOrderId) {
                await cancelQrPayment();
              }

              setShowPaymentModal(false);
              // QR ödeme state'lerini temizle
              setIsQrPaymentActive(false);
              setQrPaymentData(null);
              setQrPaymentStatus('');
              setCurrentOrderId(null);
              // QR ödeme interval'ını temizle
              if (qrPaymentInterval) {
                clearInterval(qrPaymentInterval);
                setQrPaymentInterval(null);
              }
            }}
          >
            İptal
          </Button>
          {paymentType === 'qr' && !isQrPaymentActive && (
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={processQuickPayment}
              disabled={isProcessingPayment}
            >
              📱 QR ile Ödeme
            </Button>
          )}

          {paymentType === 'qr' && isQrPaymentActive && (
            <Button
              variant="warning"
              size="lg"
              fullWidth
              disabled
            >
              ⏳ Ödeme Bekleniyor...
            </Button>
          )}

          {paymentType !== 'qr' && (
            isWaitingForCardPayment ? (
              <Button
                variant="warning"
                size="lg"
                fullWidth
                disabled
              >
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  💳 Ödeme Bekleniyor...
                </div>
              </Button>
            ) : (
              <Button
                variant="success"
                size="lg"
                fullWidth
                onClick={() => {
                  console.log('🔍 Ödemeyi Tamamla butonuna tıklandı, isProcessingPayment:', isProcessingPayment);
                  processQuickPayment();
                }}
                disabled={isProcessingPayment || isWaitingForPosResponse}
              >
                {isWaitingForPosResponse ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    POS Ödeme bekleniyor...
                  </div>
                ) : isProcessingPayment ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    İşleniyor...
                  </div>
                ) : (
                  '✅ Ödemeyi Tamamla'
                )}
              </Button>
            )
          )}
        </div>
      </Modal>

      {/* Not Ekleme Modalı */}
      <Modal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        title="Ürün Notu"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-800 dark:text-dark-text mb-2">
              {selectedProduct?.name}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Bu ürün için özel not ekleyebilirsiniz
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Not:
            </label>
            <textarea
              value={productNote}
              onChange={(e) => setProductNote(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-cafe-brown focus:border-transparent resize-none"
              rows={3}
              placeholder="Ürün notu ekleyin..."
            />
          </div>

          <div className="flex space-x-3">
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              onClick={() => setShowNoteModal(false)}
            >
              İptal
            </Button>
            <Button
              variant="success"
              size="lg"
              fullWidth
              onClick={addToCartWithNote}
            >
              Sepete Ekle
            </Button>
          </div>
        </div>
      </Modal>

      {/* Fiş Modalı */}
      <Modal
        isOpen={showReceiptModal && !!completedOrder}
        onClose={() => setShowReceiptModal(false)}
        title="Fiş"
        size="md"
      >
        <div className="text-center space-y-4">
          <div className="text-green-600 dark:text-green-400">
            <div className="text-4xl mb-3">✅</div>
            <h3 className="text-xl font-bold mb-2 text-gray-800 dark:text-gray-100">Ödeme Başarıyla Alındı!</h3>
            <p className="text-gray-600 dark:text-gray-300">
              Sipariş #{completedOrder?.id} - Hızlı Sipariş<br />
              {(completedOrder as any)?.gift_code && (completedOrder as any).gift_code.discount_amount > 0 ? (
                <>
                  Ara Toplam: {formatPrice(completedOrder?.total_amount || 0)}<br />
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    🎁 Hediye Kodu ({(completedOrder as any).gift_code.code}): -{formatPrice((completedOrder as any).gift_code.discount_amount)}
                  </span><br />
                  <span className="font-bold">Toplam: {formatPrice((completedOrder?.total_amount || 0) - (completedOrder as any).gift_code.discount_amount)}</span><br />
                </>
              ) : (
                <>Toplam: {formatPrice(completedOrder?.total_amount || 0)}<br /></>
              )}
              Ödeme Türü: {completedOrder?.payment_type?.toUpperCase()}
            </p>
          </div>

          <div className="flex space-x-3">
            {(wantsReceipt || completedOrder?.payment_type === 'kart') && (
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={printReceipt}
              >
                🖨️ Fiş Yazdır
              </Button>
            )}
            <Button
              variant="success"
              size="lg"
              fullWidth={!(wantsReceipt || completedOrder?.payment_type === 'kart')}
              onClick={finishOrder}
            >
              ✅ Tamamla
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default QuickOrder;
